import React, { useCallback, useContext, useMemo, useState } from 'react';
import { CloudUpload as CloudUploadIcon, CreateNewFolder as CreateNewFolderIcon } from '@mui/icons-material';
import { Box, Button, styled } from '@mui/material';
import { ADD_NEW_DOCUMENT, ADD_SUBFOLDER_LABEL, UPLOAD_DOCUMENT } from 'common/constants/documents';
import { DocumentDialog, RequestDocumentDialog } from 'components/Documents';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import AddFolderDialog from 'pages/Documents/components/AddFolderDialog/AddFolderDialog';
import { CurrentRequestedDocumentTableFooterProps } from './types';

const PREFIX = 'DocumentActionButtons';

const classes = {
  buttonContainer: `${PREFIX}-buttonContainer`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const DocumentActionButtons = ({ currentDocuments }: CurrentRequestedDocumentTableFooterProps) => {
  const { refreshDocuments, currentMeasurementDateId, currentCompanyMeasurementDateId, permissions } = useContext(
    InformationDashboard409AContext
  );

  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [openNewDocumentDialog, setOpenNewDocumentDialog] = useState(false);
  const [openRequestNewDocument, setOpenRequestNewDocument] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const handleClose = useCallback(() => {
    refreshDocuments?.();
    setOpenNewDocumentDialog(false);
    setOpenFolderDialog(false);
    setOpenRequestNewDocument(false);
    setDroppedFiles([]);
  }, [refreshDocuments]);

  const currentFolders = useMemo(
    () => currentDocuments?.folders?.filter(folder => folder.parent_folder === null),
    [currentDocuments]
  );

  const renderDocumentDialog = useCallback(
    () => (
      <DocumentDialog
        open={openNewDocumentDialog}
        onClose={handleClose}
        title={ADD_NEW_DOCUMENT}
        addSelectComponent={false}
        droppedFiles={droppedFiles}
        setDroppedFiles={setDroppedFiles}
        selectedMeasurementDateId={Number(currentMeasurementDateId)}
        customButtonLabel="Done"
        fromReferenceWidget={false}
      />
    ),
    [openNewDocumentDialog, handleClose, droppedFiles, currentMeasurementDateId]
  );

  const renderAddFolderDialog = useCallback(
    () => (
      <AddFolderDialog
        open={openFolderDialog}
        onClose={handleClose}
        title={ADD_SUBFOLDER_LABEL}
        currentFolders={currentFolders}
        currentDocuments={currentDocuments}
      />
    ),
    [openFolderDialog, handleClose, currentFolders, currentDocuments]
  );

  const renderRequestDocumentDialog = useCallback(
    () => (
      <RequestDocumentDialog
        open={openRequestNewDocument}
        onClose={handleClose}
        currentDocuments={currentDocuments}
        companyMeasurementDateId={Number(currentCompanyMeasurementDateId)}
      />
    ),
    [openRequestNewDocument, handleClose, currentDocuments, currentCompanyMeasurementDateId]
  );

  const handleOpenFolderDialog = useCallback(() => setOpenFolderDialog(true), []);
  const handleOpenNewDocumentDialog = useCallback(() => {
    setDroppedFiles([]);
    setOpenNewDocumentDialog(true);
  }, []);

  if (!permissions?.canUseInfoRequestLedger) {
    return null;
  }

  return (
    <Root>
      {renderDocumentDialog()}
      {renderAddFolderDialog()}
      {renderRequestDocumentDialog()}
      <Box className={classes.buttonContainer}>
        <Button startIcon={<CreateNewFolderIcon />} color="primary" onClick={handleOpenFolderDialog}>
          {ADD_SUBFOLDER_LABEL}
        </Button>
        <Button startIcon={<CloudUploadIcon />} color="primary" onClick={handleOpenNewDocumentDialog}>
          {UPLOAD_DOCUMENT}
        </Button>
      </Box>
    </Root>
  );
};

export default DocumentActionButtons;
